import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import {
	Badge,
	CustomAlert,
	CustomButton,
	CustomSwitch,
	CustomTable,
	Text,
} from '../../../../../../shared/uiComponents';
import { errorNormalizer } from '../../../../../../shared/Helpers/functions';
import { useScreenWidth } from '../../../../../../shared/Helpers/hooks';
import { BACKGROUND, TEXT } from '../../../../../../shared/Helpers/colors';
import { AdminTypes } from '../../../../../../components/Menu';
import { MasteredTableBody, MasteredTableHeaders } from '../../../MasteredTargets/tableInfo';
import { ProgramHeaders, ProgramHeadersBT, ProgramTableBody } from '../../tableInfo';
import EditProgram from '../../Actions/editProgram';
import CopyProgram from '../../Actions/copyProgram';
import DeleteProgram from '../../Actions/deleteProgram';
import ArchiveComponent from '../../Actions/archive';
import UnarchiveComponent from '../../Actions/unarchive';
import AddProgramContent from '../../Actions/addProgramContent';
import Targets from '../../Targets';
import { ProgramsContext } from '../../ProgramsContext';

import { DispatchProperties, useSelector } from '../../../../../../redux/store';
import { isElcFunder } from '../../../../../../redux/API/constants';
import { SkillProperties } from '../../../../../../redux/API/ClientAPIHelpers/skillAreaProperties';
import { getPrograms } from '../../../../../../redux/State/clientSlice/clientProgramSlice';
import { getMasteredTargets } from '../../../../../../redux/State/clientSlice/programTargetSlice';
import { ProgramDataProperties } from '../../../../../../redux/API/ClientAPIHelpers/clientProgramProperties';
import { TargetProperties } from '../../../../../../redux/API/ClientAPIHelpers/programTargetsProperties';

interface SkillAreaContentProps {
	item: SkillProperties;
}

const MaintenanceMobileCard = ({ item }: { item: TargetProperties }) => {
	const statusColor = item.status.name === 'Mastered' ? 'success' : 'primary';

	return (
		<div className="maintenanceMobileCard">
			<div className="maintenanceMobileCardHeader">
				<Text title={item.name} type="psm" fontWeight="medium" color={TEXT.PRIMARY} />
				<Badge label={item.status.name} sizeType="sm" type={statusColor} />
			</div>
			<div className="maintenanceMobileCardBody">
				<div className="maintenanceMobileCardBodyItem">
					<Text title="Date opened:" type="pxs" fontWeight="regular" color={TEXT.TERTIARY} />
					<Text
						title={dayjs(item.dateOpened).format('MM/DD/YYYY')}
						type="psm"
						fontWeight="medium"
						color={TEXT.SECONDARY}
					/>
				</div>
				<div className="maintenanceMobileCardBodyItem">
					<Text title="Date Mastered:" type="pxs" fontWeight="regular" color={TEXT.TERTIARY} />
					<Text
						title={dayjs(item.dateMastered).format('MM/DD/YYYY')}
						type="psm"
						fontWeight="medium"
						color={TEXT.SECONDARY}
					/>
				</div>
				<div className="maintenanceMobileCardBodyItem">
					<Text title="Target Type:" type="pxs" fontWeight="regular" color={TEXT.TERTIARY} />
					<Text title={item.type.name} type="psm" fontWeight="medium" color={TEXT.SECONDARY} />
				</div>
			</div>
		</div>
	);
};

export const MaintenanceContent = ({ item }: SkillAreaContentProps) => {
	const skillAreaId = item.id;
	const dispatch = useDispatch<DispatchProperties>();
	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;

	const [loading, setLoading] = useState<boolean>(false);
	const [masteredTargets, setMasteredTargets] = useState<TargetProperties[]>([]);

	useEffect(() => {
		setLoading(true);
		dispatch(getMasteredTargets(skillAreaId))
			.then(unwrapResult)
			.then((response) => setMasteredTargets(response))
			.catch(errorNormalizer)
			.finally(() => setLoading(false));
	}, [dispatch, skillAreaId]);

	if (isMobile) {
		return (
			<div className="maintenanceMobileCardContainer">
				{masteredTargets.map((target) => (
					<MaintenanceMobileCard key={target.id} item={target} />
				))}
			</div>
		);
	}

	return (
		<CustomTable
			headers={MasteredTableHeaders}
			TableBody={MasteredTableBody}
			data={{ query: masteredTargets }}
			loading={loading}
			hasPagination={false}
			hasMobile={false}
			style={{
				borderRadius: '13px',
				border: `2px solid ${BACKGROUND.PRIMARY[100]}`,
			}}
		/>
	);
};

const ProgramsMobileCard = ({ item, isBT }: { isBT: boolean; item: ProgramDataProperties }) => {
	const [open, setOpen] = useState<boolean>(false);

	const openHandler = () => setOpen(true);
	const closeHandler = () => setOpen(false);

	return (
		<>
			<CustomAlert
				title={item.name}
				open={open}
				onClose={closeHandler}
				Content={() => <Targets programId={item.id} />}
				maxWidth="lg"
			/>

			<div className="programsMobileCard">
				<div className="programsMobileCardHeader" onClick={openHandler}>
					<Text title={item.name} type="psm" fontWeight="medium" color={TEXT.PRIMARY} />
				</div>
				<div className="programsMobileCardBody">
					<div className="programsMobileCardBodyItem">
						<Text title="Open Targets:" type="pxs" fontWeight="regular" color={TEXT.TERTIARY} />
						<Text title={item.openTargets} type="psm" fontWeight="medium" color={TEXT.SECONDARY} />
					</div>
					<div className="programsMobileCardBodyItem">
						<Text title="Mastered Targets:" type="pxs" fontWeight="regular" color={TEXT.TERTIARY} />
						<Text title={item.masteredTargets} type="psm" fontWeight="medium" color={TEXT.SECONDARY} />
					</div>
					<div className="programsMobileCardBodyItem">
						<Text title="Total Targets" type="pxs" fontWeight="regular" color={TEXT.TERTIARY} />
						<Text title={item.totalTargets} type="psm" fontWeight="medium" color={TEXT.SECONDARY} />
					</div>
				</div>
				{!isBT && (
					<div className="programsMobileCardFooter">
						<DeleteProgram data={item} onlyIcon />
						<div className="programsMobileCardFooterActions">
							{item.isArchived ? (
								<UnarchiveComponent data={item} onlyIcon />
							) : (
								<ArchiveComponent data={item} onlyIcon />
							)}
							<CopyProgram data={item} onlyIcon />
							<EditProgram data={item} onlyIcon />
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export const ProgramsContent = ({ item }: SkillAreaContentProps) => {
	const skillAreaId = item.id;
	const dispatch = useDispatch<DispatchProperties>();
	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;
	const [programs, setPrograms] = useState<ProgramDataProperties[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [includeArchived, setIncludeArchived] = useState<boolean>(false);
	const [addProgramOpen, setAddProgramOpen] = useState<boolean>(false);

	const role = useSelector((state) => state.account.role);
	const isBT = role?.section.id === AdminTypes.bt;
	const headers = isBT ? ProgramHeadersBT : ProgramHeaders;

	const showArchivedProgramsHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setIncludeArchived(checked);
	};

	const onCloseAddProgram = () => setAddProgramOpen(false);

	const fetchData = useCallback(() => {
		setLoading(true);
		dispatch(
			getPrograms({
				skillAreaId,
				includeArchived,
			}),
		)
			.then(unwrapResult)
			.then((response) => setPrograms(response))
			.catch(errorNormalizer)
			.finally(() => setLoading(false));
	}, [dispatch, includeArchived, skillAreaId]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	return (
		// TODO - memoize fetchData to avoid unnecessary re-renders
		// eslint-disable-next-line react/jsx-no-constructed-context-values
		<ProgramsContext.Provider value={{ fetchData }}>
			{isMobile ? (
				<div className="programsMobileCardContainer">
					{programs.map((program) => (
						<ProgramsMobileCard key={program.id} item={program} isBT={isBT} />
					))}
				</div>
			) : (
				<CustomTable
					headers={headers}
					TableBody={ProgramTableBody}
					data={{ query: programs }}
					loading={loading}
					hasPagination={false}
					hasMobile={false}
					style={{ overflow: 'visible' }}
				/>
			)}
			{!isBT && (
				<div className="skillAcquisitionContentActions">
					<CustomButton title="Add program" onClick={() => setAddProgramOpen(true)} loading={loading} />
					<CustomSwitch
						label="Show Archived"
						checked={includeArchived}
						onChange={showArchivedProgramsHandler}
						disabled={loading}
					/>
					<CustomAlert
						open={addProgramOpen}
						onClose={onCloseAddProgram}
						title={isElcFunder(item.funderId) ? 'Add program (ELC)' : 'Add program'}
						Content={({ onClose }) => <AddProgramContent onClose={onClose} item={item} />}
					/>
				</div>
			)}
		</ProgramsContext.Provider>
	);
};
